import React, { useState } from 'react';
import { useInfluencerStore } from '@/stores/influencerStore';
import { influencerService } from '@/services/influencerService';
import { Copy, ExternalLink, QrCode, Share2, Download } from 'lucide-react';
import toast from 'react-hot-toast';

export const ReferralLinkGenerator: React.FC = () => {
  const { referralLink, generateReferralLink, loading } = useInfluencerStore();
  const [customCode, setCustomCode] = useState('');
  const [showQR, setShowQR] = useState(false);

  const handleGenerate = async () => {
    try {
      await generateReferralLink(customCode || undefined);
      toast.success('Referral link generated successfully!');
      setCustomCode('');
    } catch (error: any) {
      toast.error(error.message || 'Failed to generate referral link');
    }
  };

  const handleCopy = async () => {
    if (referralLink?.linkUrl) {
      const success = await influencerService.copyToClipboard(referralLink.linkUrl);
      if (success) {
        toast.success('Link copied to clipboard!');
      } else {
        toast.error('Failed to copy link');
      }
    }
  };

  const handlePreview = () => {
    if (referralLink?.linkUrl) {
      window.open(referralLink.linkUrl, '_blank');
    }
  };

  const handleDownloadQR = () => {
    if (referralLink?.linkUrl) {
      const qrUrl = influencerService.generateQRCode(referralLink.linkUrl);
      const link = document.createElement('a');
      link.href = qrUrl;
      link.download = `qr-${referralLink.referralCode}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleSocialShare = (platform: string) => {
    if (referralLink?.linkUrl) {
      const shareUrl = influencerService.generateSocialShareLinks(referralLink.linkUrl, platform);
      window.open(shareUrl, '_blank');
    }
  };

  return (
    <div className="space-y-6">
      {/* Generate New Link */}
      {!referralLink && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium mb-4">Generate Your Referral Link</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="customCode" className="block text-sm font-medium text-gray-700 mb-2">
                Custom Code (Optional)
              </label>
              <input
                type="text"
                id="customCode"
                value={customCode}
                onChange={(e) => setCustomCode(e.target.value)}
                placeholder="e.g., TRAVEL2025, YOURNAME"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-sm text-gray-500">
                Leave empty to generate a random code
              </p>
            </div>
            <button
              onClick={handleGenerate}
              disabled={loading.referralLink}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading.referralLink ? 'Generating...' : 'Generate Referral Link'}
            </button>
          </div>
        </div>
      )}

      {/* Existing Link */}
      {referralLink && (
        <div className="space-y-6">
          {/* Link Display */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-medium mb-4">Your Referral Link</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                <input
                  type="text"
                  value={referralLink.linkUrl}
                  readOnly
                  className="flex-1 bg-transparent border-none outline-none text-sm"
                />
                <button
                  onClick={handleCopy}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  title="Copy to clipboard"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
              
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handlePreview}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>Preview</span>
                </button>
                <button
                  onClick={() => setShowQR(!showQR)}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <QrCode className="h-4 w-4" />
                  <span>QR Code</span>
                </button>
                <button
                  onClick={handleDownloadQR}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <Download className="h-4 w-4" />
                  <span>Download QR</span>
                </button>
              </div>
            </div>
          </div>

          {/* QR Code */}
          {showQR && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium mb-4">QR Code</h3>
              <div className="flex justify-center">
                <img
                  src={influencerService.generateQRCode(referralLink.linkUrl)}
                  alt="QR Code"
                  className="border border-gray-200 rounded-lg"
                />
              </div>
            </div>
          )}

          {/* Social Media Sharing */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-medium mb-4">Share on Social Media</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <button
                onClick={() => handleSocialShare('twitter')}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-500"
              >
                <Share2 className="h-4 w-4" />
                <span>Twitter</span>
              </button>
              <button
                onClick={() => handleSocialShare('facebook')}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Share2 className="h-4 w-4" />
                <span>Facebook</span>
              </button>
              <button
                onClick={() => handleSocialShare('linkedin')}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800"
              >
                <Share2 className="h-4 w-4" />
                <span>LinkedIn</span>
              </button>
              <button
                onClick={() => handleSocialShare('whatsapp')}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              >
                <Share2 className="h-4 w-4" />
                <span>WhatsApp</span>
              </button>
            </div>
          </div>

          {/* Link Statistics */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-medium mb-4">Link Statistics</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {referralLink.clicksCount.toLocaleString()}
                </div>
                <div className="text-sm text-gray-500">Total Clicks</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {referralLink.signupsCount.toLocaleString()}
                </div>
                <div className="text-sm text-gray-500">Signups</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {referralLink.conversionsCount.toLocaleString()}
                </div>
                <div className="text-sm text-gray-500">Conversions</div>
              </div>
            </div>
          </div>

          {/* Generate New Link */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-medium mb-4">Generate New Link</h3>
            <p className="text-sm text-gray-600 mb-4">
              You can generate a new referral link with a different code. This will deactivate your current link.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Generate New Link
            </button>
          </div>
        </div>
      )}
    </div>
  );
}; 